import JSZip from "jszip";
import { InputFormat, OutputFormat, outputFormatsLabels } from "../pandoc";
import { Rule } from "../rules";
import { getManifest } from "./utils";

const converterGeneratorAssetsFolder : string = "converter-generator-assets"

const installerFilename = `install.sh`
const rulesFilename = `rules.json`

const shBase: string = `#!/bin/bash

: '
@file This file converter was automatically generated by OutDesign.
@author Yann Trividic
@license GPLv3
@see https://codeberg.org/outdesign
'

usage() {
  echo "Usage: $0 INPUT"
  exit 1
}

`

export async function generateConverter(
    inputFormat: "idml" | InputFormat,
    outputFormat: OutputFormat = "markdown",
    inputFile: File,
    rules: Rule[],
    idml2docbookOptions: Record<string, string | boolean> | undefined
    ) {

    let sh: string = shBase;
    let name: string = `${inputFormat}2${outputFormat}`

    const pandocOptions = ["--wrap=none"]
    const pandocCommand = generatePandocCommand(inputFormat, outputFormat, pandocOptions)

    if(inputFormat === "idml") {
        if(!idml2docbookOptions) throw new Error("idml2docbook options are missing. Aborting converter generation.")
        const idml2docbookCommand = generateIdml2docbookCommand(idml2docbookOptions)
        sh = sh + `${idml2docbookCommand} | \\\n${pandocCommand}`
    } else sh = sh + `${pandocCommand} $1`

    // create a new ZIP
    const zip = new JSZip();

    // add the shell script and the rules
    zip.file(`${name}.sh`, sh);
    zip.file(rulesFilename, JSON.stringify(rules, null, 2));

    // add the installation script
    if(inputFormat === "idml") {
        let installer: string = await (await fetch(`${converterGeneratorAssetsFolder}/install-idml2docbook.sh`)).text();
        installer = installer + `\nchmod +x ${name}.sh`
        zip.file(installerFilename, installer);
    }

    // add the README
    const readme: string = generateReadme(name, inputFile, inputFormat, outputFormat)
    zip.file("README.md", readme)

    // add the license
    const license: string = await (await fetch(`${converterGeneratorAssetsFolder}/LICENSE.txt`)).text();
    zip.file("LICENSE", license);

    // add the input file
    zip.file(inputFile.name, inputFile)

    // add all Lua filter files to the ZIP
    // the filters' manifest gets regenerated at build time
    // or when the dev server is started.
    const manifest = await getManifest();
    for (const path of manifest.filters) {
        const content = await (await fetch(`/${path}`)).text();
        const relativePath = path.replace(`${converterGeneratorAssetsFolder}/pandoc-lua-filters/`, "filters/");
        zip.file(relativePath, content);
    }

    // generate the ZIP file
    const blob = await zip.generateAsync({ type: "blob" });

    downloadZip(blob, name)
}

function generateIdml2docbookCommand(
    idml2docbookOptions: Record<string, string | boolean>
    ): string {

    const base: string = "idml2docbook/bin/idml2docbook $1"

    // Correctly formatting the options
    const opts: Array<string> = Object.keys(idml2docbookOptions).map(opt => {
        const flag = `--${opt.replace("_", "-")}`
        if (typeof idml2docbookOptions[opt] === "boolean") {
            return flag
        } else return `${flag} ${idml2docbookOptions[opt]}`
    })

    const idml2docbookCommand = [base, ...opts].join(" \\\n\t")
    return idml2docbookCommand;
}

function generatePandocCommand(
    inputFormat: InputFormat | "idml",
    outputFormat: OutputFormat,
    pandocOptions: Array<string>
    ): string {

    const base: string = "pandoc"
    const opts: Array<string> = [
        `-f ${inputFormat === "idml" ? "docbook" : inputFormat}`,
        `-t ${outputFormat}`,
        ...pandocOptions,
        `--lua-filter filters/roles-to-classes-filter/roles-to-classes.lua`,
        `--lua-filter filters/outdesign-filters/map.lua`,
        `-M map=rules.json`
    ]

    return [base, ...opts].join(" \\\n\t")
}

async function downloadZip(blob: Blob, name: string) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${name}.zip`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};

function generateReadme(name: string, inputFile: File, inputFormat: "idml" | InputFormat, outputFormat: OutputFormat): string {
    let installation: string
    const base: string = `# ${name}

This converter was generated by [OutDesign](https://codeberg.org/outdesign). It takes ${inputFormat === "idml" ? "InDesign IDML" : outputFormatsLabels[inputFormat as OutputFormat].label} files as input, and generates ${outputFormatsLabels[outputFormat].label} files as output.

The transformations to be applied on your document are stored in the \`${rulesFilename}\`, and the conversion command is in \`${name}.sh\`. Those two files are basically the only two things you need to edit to adjust your converter, you do not need the OutDesign web interface anymore. To modify these files, please refer to the [Pandoc manual](https://pandoc.org/MANUAL.html) and to [OutDesign operators's documentation](https://outdesign.deborderbollore.fr/en/3_usage.html#correspondance-des-styles).

## Installation

`
    if(inputFormat === "idml") {
        installation = `In order to use this converter, you must give execution rights to \`${installerFilename}\`, and then execute it. Everything this script installs will only exist in this folder. To delete  Open a terminal and enter these two commands:

\`\`\`sh
chmod +x ${installerFilename}
sh ${installerFilename}
\`\`\`

It installs the latest version \`[idml2docbook](https://codeberg.org/outdesign/idml2docbook)\`. For this installation process to succeed, your environment must already have:

* Python >= 3.x
* Java >= 1.7
* git
* [Pandoc](https://pandoc.org/installing.html) >= 3.9

## Usage

After a successful install, you can use test your converter on the file you used in the interface:

\`\`\`sh
sh ${name}.sh ${inputFile.name}
\`\`\`
`
    } else {
        installation = `In order to use this converter, you must have [Pandoc (>= 3.9)](https://pandoc.org/installing.html) installed in your environment. Then, you need to give execution rights to your custom converter:

\`\`\`sh
chmod +x ${name}.sh
\`\`\`

## Usage

After a successful install, you can use test your converter on the file you used in the interface:

\`\`\`sh
sh ${name}.sh ${inputFile.name}
\`\`\`
`
    }

    return base + installation + "\nEnjoy!"

}